import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import { ShopifyProduct, ShopifyCollection, Cart } from '../types/shopify';

class ShopifyService {
  private client: any;

  constructor() {
    // Initialize with environment variables using new Shopify client
    this.client = createStorefrontApiClient({
      storeDomain: process.env.REACT_APP_SHOPIFY_DOMAIN || 'your-shop-name.myshopify.com',
      apiVersion: '2026-01',
      publicAccessToken: process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN || '86f5de9aa864a0c8083885c92ee4acc8'
    });
  }

  async getProducts(first: number = 20, after?: string): Promise<ShopifyProduct[]> {
    try {
      const query = `
        query getProducts($first: Int!, $after: String) {
          products(first: $first, after: $after) {
            edges {
              node {
                id
                title
                handle
                description
                images(first: 20) {
                  edges {
                    node {
                      id
                      url
                      altText
                      width
                      height
                    }
                  }
                }
                variants(first: 100) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      selectedOptions {
                        name
                        value
                      }
                      availableForSale
                      image {
                        id
                        url
                        altText
                        width
                        height
                      }
                    }
                  }
                }
                priceRange {
                  minVariantPrice {
                    amount
                    currencyCode
                  }
                  maxVariantPrice {
                    amount
                    currencyCode
                  }
                }
                availableForSale
                tags
              }
            }
          }
        }
      `;

      const { data } = await this.client.request(query, { variables: { first, after } });
      
      if (!data || !data.products) {
        console.error('No products data returned from Shopify');
        return [];
      }
      
      return data.products.edges.map((edge: any) => this.transformProduct(edge.node));
    } catch (error: any) {
      console.error('Error fetching products:', error);
      if (error.message) {
        console.error('Error details:', error.message);
      }
      return [];
    }
  }

  async getProduct(handle: string): Promise<ShopifyProduct | null> {
    try {
      const query = `
        query getProduct($handle: String!) {
          productByHandle(handle: $handle) {
            id
            title
            handle
            description
            images(first: 20) {
              edges {
                node {
                  id
                  url
                  altText
                  width
                  height
                }
              }
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  selectedOptions {
                    name
                    value
                  }
                  availableForSale
                  image {
                    id
                    url
                    altText
                    width
                    height
                  }
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
            availableForSale
            tags
          }
        }
      `;

      const { data } = await this.client.request(query, { variables: { handle } });
      return data.productByHandle ? this.transformProduct(data.productByHandle) : null;
    } catch (error) {
      console.error('Error fetching product:', error);
      return null;
    }
  }

  async getCollections(): Promise<ShopifyCollection[]> {
    try {
      const query = `
        query getCollections($first: Int!, $sortKey: CollectionSortKeys, $reverse: Boolean) {
          collections(first: $first, sortKey: $sortKey, reverse: $reverse) {
            edges {
              node {
                id
                title
                handle
                description
                image {
                  id
                  url
                  altText
                  width
                  height
                }
                metafield(namespace: "custom", key: "collection_images") {
                  references(first: 20) {
                    edges {
                      node {
                        ... on MediaImage {
                          id
                          image {
                            id
                            url
                            altText
                            width
                            height
                          }
                        }
                      }
                    }
                  }
                }
                products(first: 10) {
                  edges {
                    node {
                      id
                      title
                      handle
                      description
                      images(first: 1) {
                        edges {
                          node {
                            id
                            url
                            altText
                            width
                            height
                          }
                        }
                      }
                      priceRange {
                        minVariantPrice {
                          amount
                          currencyCode
                        }
                      }
                      availableForSale
                      tags
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const { data, errors } = await this.client.request(query, { 
        variables: { 
          first: 20,
          sortKey: 'UPDATED_AT',
          reverse: true  // Newest (most recently updated) first
        } 
      });
      
      if (errors) {
        console.error('GraphQL errors fetching collections:', errors);
        return [];
      }
      
      if (!data || !data.collections) {
        console.error('No collections data in response');
        return [];
      }
      
      return data.collections.edges.map((edge: any) => this.transformCollection(edge.node));
    } catch (error) {
      console.error('Error fetching collections:', error);
      return [];
    }
  }

  async getCollection(handle: string): Promise<ShopifyCollection | null> {
    try {
      const query = `
        query getCollection($handle: String!) {
          collectionByHandle(handle: $handle) {
            id
            title
            handle
            description
            image {
              id
              url
              altText
              width
              height
            }
            metafield(namespace: "custom", key: "collection_images") {
              references(first: 50) {
                edges {
                  node {
                    ... on MediaImage {
                      id
                      image {
                        id
                        url
                        altText
                        width
                        height
                      }
                    }
                  }
                }
              }
            }
            products(first: 50) {
              edges {
                node {
                  id
                  title
                  handle
                  description
                  images(first: 20) {
                    edges {
                      node {
                        id
                        url
                        altText
                        width
                        height
                      }
                    }
                  }
                  variants(first: 100) {
                    edges {
                      node {
                        id
                        title
                        price {
                          amount
                          currencyCode
                        }
                        selectedOptions {
                          name
                          value
                        }
                  availableForSale
                  image {
                          id
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                  priceRange {
                    minVariantPrice {
                      amount
                      currencyCode
                    }
                    maxVariantPrice {
                      amount
                      currencyCode
                    }
                  }
                  availableForSale
                  tags
                }
              }
            }
          }
        }
      `;

      const { data } = await this.client.request(query, { variables: { handle } });
      return data.collectionByHandle ? this.transformCollection(data.collectionByHandle) : null;
    } catch (error) {
      console.error('Error fetching collection:', error);
      return null;
    }
  }

  async createCart(): Promise<Cart> {
    try {
      const mutation = `
        mutation cartCreate {
          cartCreate {
            cart {
              id
              checkoutUrl
              totalQuantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        price {
                          amount
                          currencyCode
                        }
                        product {
                          id
                          title
                          handle
                        }
                        image {
                          id
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const { data, errors } = await this.client.request(mutation);
      
      if (errors) {
        console.error('GraphQL errors when creating cart:', errors);
        throw new Error(`GraphQL errors: ${JSON.stringify(errors)}`);
      }

      if (!data || !data.cartCreate || !data.cartCreate.cart) {
        console.error('Invalid response data when creating cart:', data);
        throw new Error('Invalid response from cart creation API');
      }

      console.log('Cart created:', data.cartCreate.cart);
      return this.transformCart(data.cartCreate.cart);
    } catch (error) {
      console.error('Error creating cart:', error);
      throw error;
    }
  }

  async addToCart(cartId: string, variantId: string, quantity: number): Promise<Cart> {
    try {
      const mutation = `
        mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart {
              id
              checkoutUrl
              totalQuantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        price {
                          amount
                          currencyCode
                        }
                        product {
                          id
                          title
                          handle
                        }
                        image {
                          id
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const variables = {
        cartId,
        lines: [{
          merchandiseId: variantId,
          quantity
        }]
      };

      console.log('Cart mutation variables:', variables);
      const { data, errors } = await this.client.request(mutation, { variables });
      
      if (errors) {
        console.error('GraphQL errors:', errors);
        throw new Error(`GraphQL errors: ${JSON.stringify(errors)}`);
      }

      if (!data || !data.cartLinesAdd || !data.cartLinesAdd.cart) {
        console.error('Invalid response data:', data);
        throw new Error('Invalid response from cart API');
      }

      console.log('Cart response:', data.cartLinesAdd.cart);
      
      try {
        const transformedCart = this.transformCart(data.cartLinesAdd.cart);
        console.log('Transformed cart:', transformedCart);
        return transformedCart;
      } catch (transformError) {
        console.error('Error transforming cart:', transformError);
        console.error('Cart data that failed to transform:', data.cartLinesAdd.cart);
        throw transformError;
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  async updateCartLine(cartId: string, lineItemId: string, quantity: number): Promise<Cart> {
    try {
      const mutation = `
        mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
          cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart {
              id
              checkoutUrl
              totalQuantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        price {
                          amount
                          currencyCode
                        }
                        product {
                          id
                          title
                          handle
                        }
                        image {
                          id
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const variables = {
        cartId,
        lines: [{
          id: lineItemId,
          quantity
        }]
      };

      const { data } = await this.client.request(mutation, { variables });
      return this.transformCart(data.cartLinesUpdate.cart);
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  async removeFromCart(cartId: string, lineItemId: string): Promise<Cart> {
    try {
      const mutation = `
        mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart {
              id
              checkoutUrl
              totalQuantity
              cost {
                totalAmount {
                  amount
                  currencyCode
                }
              }
              lines(first: 100) {
                edges {
                  node {
                    id
                    quantity
                    merchandise {
                      ... on ProductVariant {
                        id
                        title
                        price {
                          amount
                          currencyCode
                        }
                        product {
                          id
                          title
                          handle
                        }
                        image {
                          id
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const variables = {
        cartId,
        lineIds: [lineItemId]
      };

      const { data } = await this.client.request(mutation, { variables });
      return this.transformCart(data.cartLinesRemove.cart);
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  }

  private transformProduct(product: any): ShopifyProduct {
    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      description: product.description,
      images: product.images ? product.images.edges.map((edge: any) => ({
        id: edge.node.id,
        url: edge.node.url,
        altText: edge.node.altText,
        width: edge.node.width,
        height: edge.node.height
      })) : [],
      variants: product.variants?.edges?.map((edge: any) => ({
        id: edge.node.id,
        title: edge.node.title,
        price: {
          amount: edge.node.price?.amount || '0',
          currencyCode: edge.node.price?.currencyCode || 'USD'
        },
        selectedOptions: edge.node.selectedOptions?.map((option: any) => ({
          name: option.name,
          value: option.value
        })) || [],
        availableForSale: edge.node.availableForSale ?? true,
        quantityAvailable: 0, // Not available without unauthenticated_read_product_inventory scope
        image: edge.node.image ? {
          id: edge.node.image.id,
          url: edge.node.image.url,
          altText: edge.node.image.altText,
          width: edge.node.image.width,
          height: edge.node.image.height
        } : undefined
      })) || [],
      priceRange: {
        minVariantPrice: {
          amount: product.priceRange?.minVariantPrice?.amount || '0',
          currencyCode: product.priceRange?.minVariantPrice?.currencyCode || 'USD'
        },
        maxVariantPrice: {
          amount: product.priceRange?.maxVariantPrice?.amount || product.priceRange?.minVariantPrice?.amount || '0',
          currencyCode: product.priceRange?.maxVariantPrice?.currencyCode || product.priceRange?.minVariantPrice?.currencyCode || 'USD'
        }
      },
      availableForSale: product.availableForSale ?? true,
      tags: product.tags || []
    };
  }

  private transformCollection(collection: any): ShopifyCollection {
    // Transform collection_images metafield if present
    const collectionImages = collection.metafield?.references?.edges?.map((edge: any) => ({
      id: edge.node.image?.id || edge.node.id,
      url: edge.node.image?.url,
      altText: edge.node.image?.altText,
      width: edge.node.image?.width,
      height: edge.node.image?.height
    })).filter((img: any) => img.url) || [];

    return {
      id: collection.id,
      title: collection.title,
      handle: collection.handle,
      description: collection.description,
      image: collection.image ? {
        id: collection.image.id,
        url: collection.image.url,
        altText: collection.image.altText,
        width: collection.image.width,
        height: collection.image.height
      } : undefined,
      products: collection.products ? collection.products.edges.map((edge: any) => this.transformProduct(edge.node)) : [],
      collectionImages: collectionImages.length > 0 ? collectionImages : undefined
    };
  }

  private transformCart(cart: any): Cart {
    try {
      return {
        id: cart.id,
        checkoutUrl: cart.checkoutUrl,
        totalQuantity: cart.totalQuantity || 0,
        totalPrice: {
          amount: cart.cost?.totalAmount?.amount || '0.00',
          currencyCode: cart.cost?.totalAmount?.currencyCode || 'USD'
        },
        lines: cart.lines ? cart.lines.edges.map((edge: any) => {
          const product = edge.node.merchandise.product;
          return {
            id: edge.node.id, // Line item ID (cart line ID)
            variantId: edge.node.merchandise.id, // Variant ID
            quantity: edge.node.quantity,
            product: {
              id: product.id,
              title: product.title,
              handle: product.handle || '',
              description: product.description || '',
              images: [],
              variants: [],
              priceRange: {
                minVariantPrice: {
                  amount: edge.node.merchandise.price.amount,
                  currencyCode: edge.node.merchandise.price.currencyCode
                },
                maxVariantPrice: {
                  amount: edge.node.merchandise.price.amount,
                  currencyCode: edge.node.merchandise.price.currencyCode
                }
              },
              availableForSale: true,
              tags: []
            },
            variant: {
              id: edge.node.merchandise.id,
              title: edge.node.merchandise.title,
              price: {
                amount: edge.node.merchandise.price.amount,
                currencyCode: edge.node.merchandise.price.currencyCode
              },
              selectedOptions: [],
              availableForSale: true,
              quantityAvailable: 0,
              image: edge.node.merchandise.image ? {
                id: edge.node.merchandise.image.id,
                url: edge.node.merchandise.image.url,
                altText: edge.node.merchandise.image.altText || '',
                width: edge.node.merchandise.image.width || 0,
                height: edge.node.merchandise.image.height || 0
              } : undefined
            }
          };
        }) : []
      };
    } catch (error) {
      console.error('Error transforming cart:', error);
      console.error('Cart data:', cart);
      throw error;
    }
  }
}

export default new ShopifyService();